import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search as SearchIcon, UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { searchProfiles } from '../lib/orbitApi';
import PhoneFrame from '../components/PhoneFrame';
import StarField from '../components/StarField';

// Search the cosmos for other users by username/display name and add
// them as a friend directly from the results -- fills in the "Discovery
// is warming up" placeholder that used to sit behind the search icon.
export default function Search() {
  const nav = useNavigate();
  const { friends, addFriendById } = useApp();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState({});

  const friendIds = new Set(friends.map((f) => f.id));

  const runSearch = async (q) => {
    setQuery(q);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await searchProfiles(q);
      setResults(rows);
    } catch (err) {
      toast(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (id) => {
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await addFriendById(id);
      toast('Added to your orbit');
    } catch (err) {
      toast(err.message || 'Could not add friend');
    } finally {
      setPending((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10">
          <div className="orbit-page-header">
            <button className="press orbit-icon-button" onClick={() => nav(-1)} aria-label="Go back">
              <ArrowLeft />
            </button>
            <div className="orbit-field flex-1 flex items-center gap-2" style={{ minWidth: 0, minHeight: 40, padding: '8px 16px', background: 'rgba(255,255,255,.06)' }}>
              <SearchIcon size={18} color="#9d92c4" />
              <input
                autoFocus
                value={query}
                onChange={(e) => runSearch(e.target.value)}
                placeholder="Search the cosmos for someone…"
                className="flex-1 bg-transparent outline-none placeholder:text-[#7d769f]"
                style={{ color: '#efeaff' }}
              />
            </div>
          </div>

          <div className="p-4">
            {loading && <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Searching…</p>}
            {!loading && query.trim() && results.length === 0 && (
              <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>No one out there by that name.</p>
            )}
            {!loading && !query.trim() && (
              <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Type a username or name to find someone.</p>
            )}
            {results.map((r) => {
              const alreadyFriend = friendIds.has(r.id);
              return (
                <div key={r.id} className="orbit-surface-card flex items-center gap-3 mb-3">
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" style={{ border: '1px solid rgba(160,140,230,.4)' }} />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
                      {(r.display_name || r.username || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate" style={{ color: '#f1ecff' }}>{r.display_name}</div>
                    <div className="text-sm truncate" style={{ color: '#8079a6' }}>@{r.username}</div>
                  </div>
                  <button
                    onClick={() => handleAdd(r.id)}
                    disabled={alreadyFriend || pending[r.id]}
                    className={`press !px-4 ${alreadyFriend ? 'orbit-secondary-button' : 'orbit-primary-button'}`}
                    style={{ opacity: pending[r.id] ? 0.6 : 1 }}>
                    {alreadyFriend ? <><Check /> Orbiting</> : <><UserPlus /> Orbit</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
